import { useEffect, useRef } from 'react'
import { FormProvider } from 'react-hook-form'
import { Button } from '@pay-com/components'
import { BankAccount } from 'generated/graphql'
import { useOnboardingForm } from 'hooks'
import { ControlledFields } from 'components/ControlledFields'
import { TooltipFieldProvider } from 'components/TooltipFieldWrapper'
import { CloudSaveIndicator } from 'components/CloudSaveIndicator'
import { OnboardingSidebar } from './OnboardingSidebar'
import {
  EntityMccs,
  Individuals,
  MerchantApplicationData,
  TemporaryScreen
} from './types'

type OnboardingProps = {
  merchantApplication: MerchantApplicationData
  individuals: Individuals
  bankAccounts?: BankAccount[]
  entityMccs?: EntityMccs | null
  setTemporaryScreen: (value: TemporaryScreen | null) => void
}

export const Onboarding = ({
  merchantApplication,
  bankAccounts,
  individuals,
  entityMccs,
  setTemporaryScreen
}: OnboardingProps) => {
  const {
    methods,
    steps,
    currentStep,
    clampedIndex,
    isLastStep,
    isSubmitting,
    cloudState,
    stepStatuses,
    submitText,
    validateAndChangeStep,
    handleFormSubmit,
    handleContinue
  } = useOnboardingForm({
    merchantApplication,
    bankAccounts,
    individuals,
    entityMccs,
    setTemporaryScreen
  })

  const outerRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    outerRef.current?.scrollTo({ top: 0 })
    contentRef.current?.scrollTo({ top: 0 })
  }, [clampedIndex])

  return (
    <div
      ref={outerRef}
      className="relative mx-auto flex h-screen min-w-80 flex-col items-center overflow-auto bg-white md:flex-row md:items-stretch md:overflow-hidden"
    >
      <div className="absolute right-6 top-6 z-50 hidden md:block">
        <CloudSaveIndicator cloudState={cloudState} />
      </div>
      <OnboardingSidebar
        steps={steps}
        currentStepIndex={clampedIndex}
        stepStatuses={stepStatuses}
        onStepClick={validateAndChangeStep}
        cloudState={cloudState}
      />
      <div
        ref={contentRef}
        className="flex-1 p-4 pb-32 md:max-h-screen md:overflow-auto md:p-10 md:pb-40 md:pl-16 md:pt-28 2xl:pl-[7.5rem] 3xl:pl-10"
      >
        <div className="flex max-w-[30rem] flex-col md:min-h-[40rem] 3xl:mx-auto 3xl:-translate-x-52">
          <h4 className="text-2xl font-semibold md:tracking-wide">
            {currentStep.title}
          </h4>
          <div className="mb-10">
            {currentStep.description && (
              <p className="!text-xs text-gray-600 md:!text-sm md:!leading-4.5">
                {currentStep.description}
              </p>
            )}
            {currentStep.headerAdditionalContent}
          </div>
          {currentStep.formConfig && (
            <FormProvider {...methods}>
              <TooltipFieldProvider>
                <form
                  onSubmit={isLastStep ? handleFormSubmit : undefined}
                  key={currentStep.id}
                  className="flex flex-1 flex-col gap-6"
                >
                  <ControlledFields fields={currentStep.formConfig.fields} />
                  <div className="mt-auto flex flex-col gap-10">
                    {currentStep.submitAdditionalContent}
                    <div className="flex items-center justify-end gap-3">
                      <Button
                        type={isLastStep ? 'submit' : 'button'}
                        variant="primary"
                        isLoading={isSubmitting}
                        onClick={isLastStep ? undefined : handleContinue}
                        className="w-full"
                      >
                        {submitText}
                      </Button>
                    </div>
                  </div>
                </form>
              </TooltipFieldProvider>
            </FormProvider>
          )}
        </div>
      </div>
    </div>
  )
}
